import CloseIcon from '@mui/icons-material/Close';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { CircularProgress, IconButton, InputAdornment, TextField } from '@mui/material';
import { Row, Table } from '@tanstack/react-table';
import { L } from 'harmony-language';
import React, { FC, useEffect, useState } from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { useTractorTrailer } from '../../../../api/queries/use-tractor-trailer';
import { Driver, Load, OrganizationLocation } from '../../../../types';
import { AgisticsTooltip } from '../../../shared/agistics-tooltip';
import { useDebounce } from '../../../shared/hooks/use-debounce';
import { searchByDriver, searchByLoadDate, searchByLoadId, searchByOrderNumber, searchByStopCargo, searchByStopCustomerOrderNumber, searchByStopDates, searchByStopLocation, searchByStopNotes, searchByTractor, searchByTrailer } from '../../../shared/table/utils/search-functions';
import { GlobalFilter, GlobalFilterObject } from '../../hooks/use-draft-load-table';

export type ArbitraryTextFilter = Omit<GlobalFilterObject<'arbitraryTextFilter', string>, 'label'>;

interface ArbitraryTextFilterProps {
    table: Table<Load>;
}

export const ArbitraryTextFilter: React.FC<ArbitraryTextFilterProps> = (props) => {
    const { table } = props;
    const { tractors, trailers , isLoadingTractors, isLoadingTrailers } = useTractorTrailer();
    // const { data: loadingTeams, isLoading: isLoadingLoadingTeams } = useOrganizationQuery(OrgQueryKeys.loadingTeams);
    const { data: organizationLocations = [], isLoading: isLoadingLocations } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { data: drivers, isLoading: isLoadingDrivers } = useOrganizationQuery<Driver[]>(OrgQueryKeys.drivers);
    const isLoading = isLoadingTractors || isLoadingTrailers || isLoadingLocations || isLoadingDrivers;

    const [search, setSearch] = useState('');
    const { debouncedValue, isDebouncing } = useDebounce(search);

    function arbitraryTextFilterFn(row: Row<Load>, value: string) {
        const load = row.original;
        // keep short circuit return for better performance
        // also order of searches by what is 'most likely' to be searched for
        return !isLoading && (
            searchByOrderNumber(load, value) ||
            searchByStopLocation(load, value, organizationLocations) ||
            searchByDriver(load, value, drivers) ||
            searchByStopCargo(load, value) ||
            searchByStopCustomerOrderNumber(load, value) ||
            searchByLoadDate(load, value) ||
            searchByStopDates(load, value) ||
            searchByTrailer(load, value, trailers) ||
            searchByTractor(load, value, tractors) ||
            // searchByStopLoadingTeam(load, value, loadingTeams) ||
            searchByLoadId(load, value)) ||
            searchByStopNotes(load, value);
    }

    useEffect(() => {
        table.setGlobalFilter((prevGlobalFilter: GlobalFilter): GlobalFilter => {
            const globalFilterWithoutTextFilter = prevGlobalFilter?.filter(item => item.type !== 'arbitraryTextFilter');

            if (debouncedValue) {
                const newValue = {
                    type: 'arbitraryTextFilter',
                    value: debouncedValue,
                    filterFn: arbitraryTextFilterFn
                } satisfies ArbitraryTextFilter;
                return [...(globalFilterWithoutTextFilter || []), newValue];
            } else {
                return globalFilterWithoutTextFilter;
            }
        });
    }, [debouncedValue]);

    return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '.3rem' }}>
            <AgisticsTooltip
                tooltip={<ArbitraryTextFilterTooltipContent />}
                placement='left'
            >
                <InfoOutlined />
            </AgisticsTooltip>
            <TextField
                variant='standard'
                value={search}
                label={L.search()}
                onChange={(e) => {
                    setSearch(e.target.value);
                }}
                sx={{
                    width: '15rem',
                }}
                InputProps={{
                    endAdornment: isDebouncing
                    ? <InputAdornment position='end'><CircularProgress size={'1em'} /></InputAdornment>
                    : debouncedValue &&
                    <InputAdornment position='end'>
                        <IconButton size='small' onClick={() => setSearch('')}>
                            <CloseIcon fontSize='inherit' />
                        </IconButton>
                    </InputAdornment>
                }}
            />
        </div>
    );
};

// this is split now as tooltip does not like to 'hover' over header because it's position: sticky
const ArbitraryTextFilterTooltipContent: FC = () => {
    return (
        <div style={{ display: 'flex', gap: '.5rem' }}>
            <div>
                <div>{`${L.loadId()}: ${L.id()}`}</div>
                <div>{`${L.driver()}: ${L.name()}`}</div>
                <div>{`${L.tractor()}: ${L.name()}`}</div>
                <div>{`${L.trailer()}: ${L.name()}`}</div>
                <div>{`${L.date()}: YYYY-MM-DD`}</div>
                <div>{`${L.stopArrivalTime()}: YYYY-MM-DD`}</div>
                <div>{`${L.readyTime()}: YYYY-MM-DD`}</div>
                <div>{`${L.requiredBegin()}: YYYY-MM-DD`}</div>
                <div>{`${L.requiredEnd()}: YYYY-MM-DD`}</div>
            </div>
            <div>
                <div>{`${L.cargo()}: ${L.cargoType()} - ${L.productionPlan()} - ${L.lotId()}`}</div>
                <div>{`${L.customerOrderNumber()}: ${L.customerOrderNumber()}`}</div>
                <div>{`${L.orderNumber()}: ${L.orderNumber()}`}</div>
                <div>{`${L.location()}: ${L.name()} - ${L.description()} - ${L.subLocation()}`}</div>
                {/* <div>{`${L.loadingTeam()}: ${L.name()}`}</div> */}
            </div>
        </div>
    );
}